import { upcomingOccasions } from '@ly360/core';
import { isoToZonedWallTime, zonedWallTimeToIso } from '@ly360/i18n';
import { ChannelIcon, Dialog, Empty, ErrorState, Loading, PageHead, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ExternalLink, Plus, RotateCw, Send, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { api } from '../lib/api';
import { safeHref } from '../lib/links';
import { useAuth } from '../lib/auth';
import { iconFor } from '../integrations/Connect';
import { DialogButtons } from '../retail/ui';
import {
  draftIssues,
  draftPayload,
  emptyDraft,
  PostComposer,
  useDestinations,
  type PostDraft,
} from './Composer';

export interface PostTarget {
  id: string;
  accountId: string;
  provider: string;
  accountName: string;
  body: string | null;
  status: 'queued' | 'publishing' | 'published' | 'failed' | 'skipped';
  url: string | null;
  error: string | null;
  publishedAt: string | null;
}
export interface Post {
  id: string;
  body: string;
  media: { url: string; mime: string }[];
  linkUrl: string | null;
  status: 'draft' | 'scheduled' | 'publishing' | 'published' | 'partial' | 'failed';
  scheduledAt: string | null;
  publishedAt: string | null;
  campaignId: string | null;
  campaignName: string | null;
  createdAt: string;
  createdByName: string | null;
  targets: PostTarget[] | null;
}

const TONE: Record<string, string> = {
  draft: 'badge-neutral',
  scheduled: 'badge',
  publishing: 'badge',
  published: 'badge-success',
  partial: 'badge-warning',
  failed: 'badge-danger',
  queued: 'badge',
  skipped: 'badge-neutral',
};
export function PostStatus({ value }: { value: string }) {
  const { t } = useTranslation();
  return <span className={`badge ${TONE[value] ?? 'badge-neutral'}`}>{t(`publishing.status.${value}`)}</span>;
}

const iso = (d: Date) => d.toISOString().slice(0, 10);
const noon = (d: string) => new Date(`${d}T12:00:00Z`);

export function PublishingPage() {
  const { t, i18n } = useTranslation();
  const fmt = useFormat();
  const { session, can } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [composing, setComposing] = useState<string | null>(null);
  // Opened from the command bar ("post … everywhere").
  const location = useLocation();
  const navigate = useNavigate();
  const [asked, setAsked] = useState<{ text: string; scheduledAt: string | null } | null>(
    () =>
      (location.state as { compose?: { text: string; scheduledAt: string | null } } | null)?.compose ?? null,
  );
  useEffect(() => {
    if ((location.state as { compose?: unknown } | null)?.compose)
      void navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);
  const [open, setOpen] = useState<Post | null>(null);
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const zone = session!.tenant.defaults.timezone;
  const dayOf = (v: string) => new Intl.DateTimeFormat('en-CA', { timeZone: zone }).format(new Date(v));
  const from = new Date(`${month}-01T00:00:00Z`);
  from.setUTCDate(from.getUTCDate() - 7);
  const to = new Date(`${month}-01T00:00:00Z`);
  to.setUTCMonth(to.getUTCMonth() + 1);
  to.setUTCDate(to.getUTCDate() + 7);
  const posts = useQuery({
    queryKey: ['posts', month],
    queryFn: () => api<{ items: Post[] }>(`/v1/posts?from=${from.toISOString()}&to=${to.toISOString()}`),
    // Follow posts that are going out now (scheduled ones become published without anyone reloading).
    refetchInterval: (q) =>
      q.state.data?.items.some(
        (p) =>
          p.status === 'publishing' ||
          (p.status === 'scheduled' && p.scheduledAt && new Date(p.scheduledAt).getTime() <= Date.now()),
      )
        ? 3000
        : 60_000,
  });
  const occasions = useMemo(
    () => upcomingOccasions(session!.tenant.country, new Date(`${month}-01T00:00:00Z`), 45),
    [month, session],
  );
  const weekStart = session!.tenant.defaults.weekStart ?? 0;
  const days = useMemo(() => {
    const first = noon(`${month}-01`);
    const lead = (first.getUTCDay() - weekStart + 7) % 7;
    const [y, m] = month.split('-').map(Number) as [number, number];
    const n = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return Array.from({ length: Math.ceil((lead + n) / 7) * 7 }, (_, i) =>
      iso(new Date(first.getTime() + (i - lead) * 86_400_000)),
    );
  }, [month, weekStart]);
  const byDay = new Map<string, Post[]>();
  for (const p of posts.data?.items ?? []) {
    const d = dayOf(p.scheduledAt ?? p.publishedAt ?? p.createdAt);
    byDay.set(d, [...(byDay.get(d) ?? []), p]);
  }
  const shift = (n: number) => {
    const d = noon(`${month}-15`);
    d.setUTCMonth(d.getUTCMonth() + n);
    setMonth(iso(d).slice(0, 7));
  };
  const today = dayOf(new Date().toISOString());

  return (
    <>
      <PageHead
        eyebrow={t('nav.marketing')}
        title={t('publishing.title')}
        subtitle={t('publishing.subtitle')}
        actions={
          can('posts:write') && (
            <button className="btn btn-grad" onClick={() => setComposing(today)} data-testid="new-post">
              <Plus size={16} />
              {t('publishing.new')}
            </button>
          )
        }
      />
      <section className="card month-view" data-testid="content-calendar">
        <div className="card-head month-head">
          <h2>
            {new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
              noon(`${month}-15`),
            )}
          </h2>
          <div className="quick-actions">
            <button
              type="button"
              className="icon-btn"
              onClick={() => shift(-1)}
              aria-label={t('calendar.month.previous')}
            >
              <ChevronLeft size={16} className="icon-dir" />
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setMonth(today.slice(0, 7))}
            >
              {t('calendar.month.today')}
            </button>
            <button
              type="button"
              className="icon-btn"
              onClick={() => shift(1)}
              aria-label={t('calendar.month.next')}
            >
              <ChevronRight size={16} className="icon-dir" />
            </button>
          </div>
        </div>
        {posts.isError && <ErrorState error={posts.error} />}
        <div className="month-scroll">
          <div className="month-grid">
            <div className="month-row">
              {days.slice(0, 7).map((d) => (
                <div key={d} className="month-wd">
                  {new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' }).format(noon(d))}
                </div>
              ))}
            </div>
            {Array.from({ length: days.length / 7 }, (_, w) => (
              <div key={w} className="month-row">
                {days.slice(w * 7, w * 7 + 7).map((d) => {
                  const occasion = occasions.find((o) => o.date === d);
                  const list = byDay.get(d) ?? [];
                  return (
                    <div
                      key={d}
                      className={`month-day cal-day${d.slice(0, 7) !== month ? ' is-other' : ''}${d === today ? ' is-today' : ''}${occasion ? ' is-occasion' : ''}`}
                      data-date={d}
                    >
                      <span className="md-num">
                        {new Intl.DateTimeFormat(lang, {
                          day: 'numeric',
                          timeZone: 'UTC',
                          numberingSystem: 'latn',
                        }).format(noon(d))}
                      </span>
                      {can('posts:write') && d >= today && (
                        <button
                          type="button"
                          className="cal-add"
                          onClick={() => setComposing(d)}
                          aria-label={t('publishing.newOn', { date: fmt.date(noon(d)) })}
                        >
                          <Plus size={12} />
                        </button>
                      )}
                      {occasion && (
                        <span className="md-occasion">{t(`retail.occasion.${occasion.key}`)}</span>
                      )}
                      {list.slice(0, 3).map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className={`cal-post is-${p.status}`}
                          onClick={() => setOpen(p)}
                          data-testid="cal-post"
                        >
                          <span className="cal-icons">
                            {[...new Set((p.targets ?? []).map((x) => x.provider))].slice(0, 4).map((pr) => (
                              <ChannelIcon key={pr} channel={iconFor(pr)} size={9} />
                            ))}
                          </span>
                          <span className="ellipsis" dir="auto">
                            {p.body || t('publishing.mediaOnly')}
                          </span>
                        </button>
                      ))}
                      {list.length > 3 && <span className="faint md-more">+{list.length - 3}</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <h2>{t('publishing.recent')}</h2>
        </div>
        {posts.isLoading && <Loading />}
        {posts.data && !posts.data.items.length && <Empty text={t('publishing.empty')} />}
        <ul className="list-plain post-list" data-testid="post-list">
          {posts.data?.items.map((p) => (
            <li key={p.id}>
              <button type="button" className="post-row" onClick={() => setOpen(p)}>
                {p.media[0] && !p.media[0].mime.startsWith('video/') ? (
                  <img src={p.media[0].url} alt="" className="post-thumb" />
                ) : (
                  <span className="post-thumb" />
                )}
                <span className="grow">
                  <span className="ellipsis" dir="auto" style={{ display: 'block' }}>
                    {p.body || t('publishing.mediaOnly')}
                  </span>
                  <span className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                    {fmt.dateTime(p.scheduledAt ?? p.publishedAt ?? p.createdAt)}
                    {p.campaignName && ` · ${p.campaignName}`}
                  </span>
                </span>
                <span className="cal-icons">
                  {(p.targets ?? []).map((x) => (
                    <span
                      key={x.id}
                      className={`target-dot is-${x.status}`}
                      title={`${x.accountName}: ${t(`publishing.status.${x.status}`)}`}
                    >
                      <ChannelIcon channel={iconFor(x.provider)} size={11} />
                    </span>
                  ))}
                </span>
                <PostStatus value={p.status} />
              </button>
            </li>
          ))}
        </ul>
      </section>
      {composing && <NewPostDialog day={composing} onClose={() => setComposing(null)} />}
      {asked && <NewPostDialog initial={asked} onClose={() => setAsked(null)} />}
      {open && <PostDialog id={open.id} onClose={() => setOpen(null)} />}
    </>
  );
}

export function NewPostDialog({
  day,
  initial,
  onClose,
}: {
  day?: string;
  /** Text (and time) asked for in the command bar: every account is ticked to start with. */
  initial?: { text: string; scheduledAt: string | null };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const destinations = useDestinations();
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: fmt.timezone }).format(new Date());
  const [draft, setDraft] = useState<PostDraft>(() => ({
    ...emptyDraft(),
    targets: [],
    ...(day && day > today ? { when: 'later' as const, scheduledAt: `${day}T10:00` } : {}),
    ...(initial
      ? {
          body: initial.text,
          ...(initial.scheduledAt
            ? {
                when: 'later' as const,
                scheduledAt: isoToZonedWallTime(initial.scheduledAt, fmt.timezone).slice(0, 16),
              }
            : {}),
        }
      : {}),
  }));
  const items = destinations.data?.items ?? [];
  // "Post … everywhere": tick every account once they have loaded.
  useEffect(() => {
    if (initial && items.length && !draft.targets.length)
      setDraft((d) => ({ ...d, targets: items.map((x) => x.id) }));
  }, [initial, items, draft.targets.length]);
  const blocked = draftIssues(draft, items).some((i) => i.errors.length) || !draft.targets.length;
  const m = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/v1/posts', {
        method: 'POST',
        body: JSON.stringify(draftPayload(draft, (w) => zonedWallTimeToIso(w, fmt.timezone))),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['posts'] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      wide
      title={t('publishing.new')}
      onClose={onClose}
      onSubmit={() => m.mutate()}
      testId="new-post-dialog"
      footer={
        <DialogButtons
          onClose={onClose}
          busy={m.isPending}
          disabled={blocked || (draft.when === 'later' && !draft.scheduledAt)}
          label={t(`publishing.submit.${draft.when}`, { count: draft.targets.length })}
        />
      }
    >
      {destinations.isLoading ? (
        <Loading />
      ) : (
        <PostComposer draft={draft} setDraft={setDraft} destinations={items} />
      )}
      {m.isError && <ErrorState error={m.error} />}
    </Dialog>
  );
}

function PostDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const { can } = useAuth();
  const q = useQuery({
    queryKey: ['posts', 'detail', id],
    queryFn: () => api<{ post: Post }>(`/v1/posts/${id}`),
    refetchInterval: (query) =>
      ['scheduled', 'publishing'].includes(query.state.data?.post.status ?? '') ? 3000 : false,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['posts'] });
  const act = useMutation({
    mutationFn: (action: 'publish_now' | 'unschedule') =>
      api(`/v1/posts/${id}`, { method: 'PATCH', body: JSON.stringify({ action }) }),
    onSuccess: refresh,
  });
  const retry = useMutation({
    mutationFn: () => api(`/v1/posts/${id}/retry`, { method: 'POST', body: '{}' }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api(`/v1/posts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  const p = q.data?.post;
  return (
    <Dialog open wide title={t('publishing.post')} onClose={onClose} testId="post-dialog">
      {q.isLoading && <Loading />}
      {p && (
        <div className="post-detail">
          <div className="post-detail-head">
            <PostStatus value={p.status} />
            <span className="faint">
              {p.publishedAt
                ? t('publishing.publishedAt', { when: fmt.dateTime(p.publishedAt) })
                : p.scheduledAt
                  ? t('publishing.scheduledFor', { when: fmt.dateTime(p.scheduledAt) })
                  : t('publishing.draftSaved')}
            </span>
            {p.campaignId && (
              <Link to={`/campaigns/${p.campaignId}`} className="btn btn-ghost btn-sm">
                {p.campaignName}
              </Link>
            )}
          </div>
          <p className="post-body" dir="auto">
            {p.body}
          </p>
          {p.media.length > 0 && (
            <div className="post-media">
              {p.media.map((m) => (
                <figure key={m.url} className="media-thumb">
                  {m.mime.startsWith('video/') ? <video src={m.url} muted /> : <img src={m.url} alt="" />}
                </figure>
              ))}
            </div>
          )}
          <table className="table" data-testid="post-targets">
            <tbody>
              {(p.targets ?? []).map((x) => (
                <tr key={x.id}>
                  <td>
                    <span className="channel-badge">
                      <ChannelIcon channel={iconFor(x.provider)} size={12} />
                      <span dir="auto">{x.accountName}</span>
                    </span>
                    {x.body && (
                      <span
                        className="faint"
                        style={{ display: 'block', fontSize: 'var(--t-xs)' }}
                        dir="auto"
                      >
                        {t('publishing.ownWording')}
                      </span>
                    )}
                  </td>
                  <td>
                    <PostStatus value={x.status} />
                    {x.error && (
                      <span className="form-error" style={{ display: 'block' }}>
                        {x.error}
                      </span>
                    )}
                  </td>
                  <td className="num">
                    {safeHref(x.url) && (
                      <a
                        href={safeHref(x.url)!}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-ghost btn-sm"
                      >
                        <ExternalLink size={13} />
                        {t('publishing.view')}
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {can('posts:publish') && (
            <div className="quick-actions">
              {['draft', 'scheduled'].includes(p.status) && (
                <button
                  className="btn btn-grad btn-sm"
                  onClick={() => act.mutate('publish_now')}
                  data-testid="publish-now"
                >
                  <Send size={14} />
                  {t('publishing.publishNow')}
                </button>
              )}
              {p.status === 'scheduled' && (
                <button className="btn btn-ghost btn-sm" onClick={() => act.mutate('unschedule')}>
                  {t('publishing.unschedule')}
                </button>
              )}
              {['partial', 'failed'].includes(p.status) && (
                <button className="btn btn-sm" onClick={() => retry.mutate()}>
                  <RotateCw size={14} />
                  {t('publishing.retry')}
                </button>
              )}
              {['draft', 'scheduled'].includes(p.status) && (
                <button className="btn btn-ghost btn-sm" onClick={() => remove.mutate()}>
                  <Trash2 size={14} />
                  {t('common.delete')}
                </button>
              )}
            </div>
          )}
          {(act.isError || retry.isError || remove.isError) && (
            <ErrorState error={act.error ?? retry.error ?? remove.error} />
          )}
        </div>
      )}
    </Dialog>
  );
}
